import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkle, Minus, Plus, FloppyDisk, Trash, X } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { API } from '../config/api';

export const SHAPES = [
  { id: 'rettangolare', label: 'Rettangolare' },
  { id: 'arco', label: 'Arco' },
  { id: 'arco_ribassato', label: 'Arco ribassato' },
  { id: 'trapezio', label: 'Trapezio' },
  { id: 'triangolo', label: 'Triangolo' },
  { id: 'oblo', label: 'Oblò' },
];

export const OPENINGS = [
  { id: 'fisso', label: 'Fisso' },
  { id: 'battente_sx', label: 'Battente sx' },
  { id: 'battente_dx', label: 'Battente dx' },
  { id: 'antaribalta_sx', label: 'Anta-ribalta sx' },
  { id: 'antaribalta_dx', label: 'Anta-ribalta dx' },
  { id: 'vasistas', label: 'Vasistas' },
  { id: 'bilico_orizzontale', label: 'Bilico orizzontale' },
  { id: 'bilico_verticale', label: 'Bilico verticale' },
  { id: 'scorrevole_sx', label: 'Scorrevole ←' },
  { id: 'scorrevole_dx', label: 'Scorrevole →' },
];
const OBLO_OPENINGS = ['fisso', 'vasistas', 'bilico_orizzontale', 'bilico_verticale'];

export const svgDataUri = (svg) => `data:image/svg+xml;utf8,${encodeURIComponent(svg || '')}`;

/** Starting point when the product has no drawing yet: its measures and number of sashes. */
export const defaultSpec = ({ width, height, sashes = 1, kind = 'finestra' } = {}) => {
  const n = Math.max(1, Math.min(6, sashes));
  const panels = n === 1 ? [{ opening: 'antaribalta_sx', size: 1 }]
    : Array.from({ length: n }, (_, i) => ({ opening: i === n - 1 ? 'antaribalta_dx' : 'battente_sx', size: 1 }));
  return {
    shape: 'rettangolare', kind,
    width_mm: parseFloat(width) || null, height_mm: parseFloat(height) || null,
    slope_side: 'sinistra', slope: 0.3, arch_rise: 0.18,
    panels,
    transom: { position: 'nessuno', size: 0.25, panels: [{ opening: 'fisso', size: 1 }] },
  };
};

const Choice = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5">
    {options.map(o => (
      <button
        key={o.id} type="button" onClick={() => onChange(o.id)}
        className={`px-2.5 py-1 rounded-sm text-xs font-medium border transition-colors ${value === o.id ? 'bg-[#0033A0] text-white border-[#0033A0]' : 'bg-white text-[#0A0F1A] border-[#E2E8F0] hover:border-[#0033A0]'}`}
        data-testid={testid ? `${testid}-${o.id}` : undefined}
      >{o.label}</button>
    ))}
  </div>
);

const Counter = ({ value, min, max, onChange, testid }) => (
  <div className="flex items-center gap-1">
    <Button type="button" variant="outline" size="sm" className="h-7 w-7 p-0" disabled={value <= min} onClick={() => onChange(value - 1)} data-testid={`${testid}-minus`}><Minus size={12} /></Button>
    <span className="w-6 text-center text-sm font-semibold" data-testid={`${testid}-value`}>{value}</span>
    <Button type="button" variant="outline" size="sm" className="h-7 w-7 p-0" disabled={value >= max} onClick={() => onChange(value + 1)} data-testid={`${testid}-plus`}><Plus size={12} /></Button>
  </div>
);

const OpeningSelect = ({ value, onChange, allowed, testid }) => (
  <select
    value={value} onChange={e => onChange(e.target.value)}
    className="h-8 w-full rounded-sm border border-[#E2E8F0] bg-white px-2 text-xs focus:border-[#0033A0] focus:outline-none"
    data-testid={testid}
  >
    {OPENINGS.filter(o => !allowed || allowed.includes(o.id)).map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
  </select>
);

const Section = ({ title, children }) => (
  <div className="space-y-1.5">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#4A5568]">{title}</p>
    {children}
  </div>
);

const resize = (panels, n, fill) => {
  const out = panels.slice(0, n);
  while (out.length < n) out.push({ opening: fill, size: 1 });
  return out;
};

export default function DrawingEditorDialog({ open, onOpenChange, initialSpec, onApply, onRemove }) {
  const [spec, setSpec] = useState(initialSpec);
  const [preview, setPreview] = useState({ svg: '', description: '' });
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [library, setLibrary] = useState([]);
  const [saveName, setSaveName] = useState('');
  const [useAsName, setUseAsName] = useState(true);
  const reqId = useRef(0);

  useEffect(() => {
    if (!open) return;
    setSpec(initialSpec);
    setPrompt('');
    setSaveName('');
    axios.get(`${API}/drawings`).then(r => setLibrary(r.data || [])).catch(() => setLibrary([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live preview, drawn by the server exactly as it will appear in the PDF
  useEffect(() => {
    if (!open || !spec) return undefined;
    const id = ++reqId.current;
    const timer = setTimeout(() => {
      axios.post(`${API}/drawings/preview`, { spec })
        .then(r => { if (id === reqId.current) setPreview({ svg: r.data.svg, description: r.data.description }); })
        .catch(() => {});
    }, 120);
    return () => clearTimeout(timer);
  }, [spec, open]);

  const set = useCallback((patch) => setSpec(s => ({ ...s, ...patch })), []);
  const setTransom = (patch) => setSpec(s => ({ ...s, transom: { ...s.transom, ...patch } }));
  const setPanel = (i, opening) => setSpec(s => ({ ...s, panels: s.panels.map((p, j) => (j === i ? { ...p, opening } : p)) }));
  const setTransomPanel = (i, opening) => setTransom({ panels: spec.transom.panels.map((p, j) => (j === i ? { ...p, opening } : p)) });

  const changeShape = (shape) => {
    if (shape === 'oblo') {
      const opening = OBLO_OPENINGS.includes(spec.panels[0]?.opening) ? spec.panels[0].opening : 'fisso';
      set({ shape, panels: [{ opening, size: 1 }], transom: { ...spec.transom, position: 'nessuno' } });
    } else {
      set({ shape });
    }
  };

  const generate = async () => {
    if (prompt.trim().length < 3) return;
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/drawings/from-text`, {
        text: prompt.trim(), width_mm: spec.width_mm || null, height_mm: spec.height_mm || null,
      });
      setSpec(data.spec);
      setPreview({ svg: data.svg, description: data.description });
      toast.success('Disegno creato: puoi correggerlo con i comandi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Non sono riuscito a creare il disegno');
    } finally {
      setGenerating(false);
    }
  };

  const saveToLibrary = async () => {
    const name = saveName.trim() || preview.description || 'Disegno';
    try {
      const { data } = await axios.post(`${API}/drawings`, { name, spec });
      setLibrary(l => [data, ...l]);
      setSaveName('');
      toast.success('Disegno salvato nella libreria');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const deleteFromLibrary = async (d) => {
    if (!window.confirm(`Eliminare "${d.name}" dalla libreria?`)) return;
    try {
      await axios.delete(`${API}/drawings/${d.id}`);
      setLibrary(l => l.filter(x => x.id !== d.id));
    } catch {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const apply = async () => {
    try {
      const { data } = await axios.post(`${API}/drawings/preview`, { spec });
      onApply(data.spec, data.svg, data.description, useAsName);
      onOpenChange(false);
    } catch {
      toast.error('Errore nel creare il disegno');
    }
  };

  if (!spec) return null;
  const isOblo = spec.shape === 'oblo';
  const hasTransom = spec.transom.position !== 'nessuno';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="drawing-editor">
        <DialogHeader>
          <DialogTitle className="font-outfit">Disegno personalizzato</DialogTitle>
          <DialogDescription>Descrivi la finestra con una frase oppure componila con i comandi: il disegno va nel preventivo e nel PDF.</DialogDescription>
        </DialogHeader>

        {/* From a sentence */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            value={prompt} onChange={e => setPrompt(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); generate(); } }}
            placeholder="Es. finestra ad arco 2 ante anta-ribalta con sopraluce fisso, 120x180"
            className="flex-1" data-testid="drawing-prompt"
          />
          <Button type="button" onClick={generate} disabled={generating || prompt.trim().length < 3} className="bg-[#0033A0] hover:bg-[#002277]" data-testid="drawing-generate">
            <Sparkle size={16} className="mr-1.5" />{generating ? 'Creo il disegno…' : 'Crea dal testo'}
          </Button>
        </div>

        <div className="grid md:grid-cols-[1fr_300px] gap-5">
          {/* Controls */}
          <div className="space-y-4">
            <Section title="Tipo">
              <Choice options={[{ id: 'finestra', label: 'Finestra' }, { id: 'portafinestra', label: 'Portafinestra' }]} value={spec.kind} onChange={kind => set({ kind })} testid="drawing-kind" />
            </Section>
            <Section title="Forma">
              <Choice options={SHAPES} value={spec.shape} onChange={changeShape} testid="drawing-shape" />
              {spec.shape === 'trapezio' && (
                <div className="flex flex-wrap items-center gap-3 pt-1 text-xs">
                  <span>Lato più alto:</span>
                  <Choice options={[{ id: 'sinistra', label: 'Sinistra' }, { id: 'destra', label: 'Destra' }]} value={spec.slope_side} onChange={slope_side => set({ slope_side })} testid="drawing-slope-side" />
                  <label className="flex items-center gap-2">Pendenza
                    <input type="range" min="0.05" max="0.8" step="0.05" value={spec.slope} onChange={e => set({ slope: parseFloat(e.target.value) })} data-testid="drawing-slope" />
                  </label>
                </div>
              )}
              {spec.shape === 'arco_ribassato' && (
                <label className="flex items-center gap-2 pt-1 text-xs">Altezza dell'arco
                  <input type="range" min="0.05" max="0.5" step="0.01" value={spec.arch_rise} onChange={e => set({ arch_rise: parseFloat(e.target.value) })} data-testid="drawing-arch-rise" />
                </label>
              )}
            </Section>
            <Section title="Misure (mm)">
              <div className="flex items-center gap-2">
                <Input type="number" inputMode="decimal" value={spec.width_mm || ''} onChange={e => set({ width_mm: parseFloat(e.target.value) || null })} placeholder="Larghezza" className="h-8 w-28" data-testid="drawing-width" />
                <span className="text-[#4A5568]">×</span>
                <Input type="number" inputMode="decimal" value={spec.height_mm || ''} onChange={e => set({ height_mm: parseFloat(e.target.value) || null })} placeholder="Altezza" className="h-8 w-28" data-testid="drawing-height" />
              </div>
            </Section>
            <Section title={isOblo ? 'Apertura' : 'Ante (da sinistra a destra)'}>
              {!isOblo && (
                <Counter value={spec.panels.length} min={1} max={6} testid="drawing-panels"
                  onChange={n => set({ panels: resize(spec.panels, n, 'fisso') })} />
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
                {spec.panels.map((p, i) => (
                  <div key={i}>
                    {!isOblo && <p className="text-[10px] text-[#4A5568] mb-0.5">Anta {i + 1}</p>}
                    <OpeningSelect value={p.opening} onChange={v => setPanel(i, v)} allowed={isOblo ? OBLO_OPENINGS : null} testid={`drawing-panel-${i}`} />
                  </div>
                ))}
              </div>
            </Section>
            {!isOblo && (
              <Section title="Sopraluce / sottoluce">
                <Choice options={[{ id: 'nessuno', label: 'Nessuno' }, { id: 'sopra', label: 'Sopraluce' }, { id: 'sotto', label: 'Sottoluce' }]}
                  value={spec.transom.position} onChange={position => setTransom({ position })} testid="drawing-transom" />
                {hasTransom && (
                  <div className="space-y-2 pt-1">
                    <div className="flex flex-wrap items-center gap-4 text-xs">
                      <span className="flex items-center gap-2">Vetri <Counter value={spec.transom.panels.length} min={1} max={4} testid="drawing-transom-panels"
                        onChange={n => setTransom({ panels: resize(spec.transom.panels, n, 'fisso') })} /></span>
                      <label className="flex items-center gap-2">Altezza
                        <input type="range" min="0.1" max="0.6" step="0.05" value={spec.transom.size} onChange={e => setTransom({ size: parseFloat(e.target.value) })} data-testid="drawing-transom-size" />
                      </label>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {spec.transom.panels.map((p, i) => (
                        <OpeningSelect key={i} value={p.opening} onChange={v => setTransomPanel(i, v)} testid={`drawing-transom-panel-${i}`} />
                      ))}
                    </div>
                  </div>
                )}
              </Section>
            )}
          </div>

          {/* Preview */}
          <div className="space-y-3">
            <div className="border border-[#E2E8F0] rounded-sm bg-[#F8FAFC] h-72 flex items-center justify-center p-4">
              {preview.svg
                ? <img src={svgDataUri(preview.svg)} alt="Anteprima disegno" className="max-h-full max-w-full" data-testid="drawing-preview" />
                : <span className="text-xs text-[#4A5568]">Anteprima…</span>}
            </div>
            <p className="text-xs text-[#4A5568] min-h-[2rem]" data-testid="drawing-description">{preview.description}</p>
            <div className="flex gap-2">
              <Input value={saveName} onChange={e => setSaveName(e.target.value)} placeholder="Nome per la libreria" className="h-8 text-xs" data-testid="drawing-save-name" />
              <Button type="button" variant="outline" size="sm" onClick={saveToLibrary} className="h-8 shrink-0" data-testid="drawing-save">
                <FloppyDisk size={14} className="mr-1" />Salva
              </Button>
            </div>
          </div>
        </div>

        {library.length > 0 && (
          <Section title="I miei disegni">
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="drawing-library">
              {library.map(d => (
                <div key={d.id} className="relative group shrink-0">
                  <button type="button" onClick={() => setSpec(d.spec)} title={d.description}
                    className="w-24 border border-[#E2E8F0] rounded-sm p-1.5 hover:border-[#0033A0] bg-white text-left"
                    data-testid={`drawing-library-${d.id}`}>
                    <div className="h-16 flex items-center justify-center"><img src={svgDataUri(d.svg)} alt="" className="max-h-full max-w-full" /></div>
                    <p className="text-[10px] mt-1 truncate">{d.name}</p>
                  </button>
                  <button type="button" onClick={() => deleteFromLibrary(d)} title="Elimina"
                    className="absolute -top-1.5 -right-1.5 hidden group-hover:flex h-5 w-5 items-center justify-center rounded-full bg-white border border-[#E2E8F0] text-red-600">
                    <X size={10} />
                  </button>
                </div>
              ))}
            </div>
          </Section>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 pt-2 border-t border-[#E2E8F0]">
          <div>
            {onRemove && (
              <Button type="button" variant="ghost" onClick={() => { onRemove(); onOpenChange(false); }} className="text-red-600 hover:bg-red-50" data-testid="drawing-remove">
                <Trash size={16} className="mr-1.5" />Torna all'icona standard
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <label className="flex items-center gap-1.5 text-xs text-[#4A5568] mr-2">
              <input type="checkbox" checked={useAsName} onChange={e => setUseAsName(e.target.checked)} data-testid="drawing-use-name" />
              Usa la descrizione come nome del prodotto
            </label>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="button" onClick={apply} className="bg-[#0033A0] hover:bg-[#002277]" data-testid="drawing-apply">Usa nel preventivo</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
